/**
 * The offer where the viewer is looking: in the picture.
 *
 * Three pieces, all pure. The card slides in when the talk reaches the offer's
 * minute and shows only what the host set on the offer card: whose note it is,
 * the short video if there is one, and the button. Closed, it folds to a small
 * tab that stays for the rest of the talk. When the talk ends, the picture gives
 * way to the same offer, larger, with one line to watch again. Nothing here says
 * anything the host did not write, so it reads right for every workspace.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { OfferJson, OfferVideo } from "../../shared/types";
import { useNoteStream } from "../lib/noteStream";
import { fileHref, withEmail } from "./WatchOffer";

/** How long the code button reads "Copied" after a press. */
const COPIED_MS = 2000;

function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] || full;
}

function mmss(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

const IcArrow = (): ReactNode => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 17 17 7M9 7h8v8" />
  </svg>
);
const IcSave = (): ReactNode => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
  </svg>
);
const IcCross = (): ReactNode => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);
const IcPlay = (): ReactNode => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);
const IcAgain = (): ReactNode => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v5h5" />
  </svg>
);

/** The words on the folded tab: the button's own, or what the kind is. */
function offerTabLabel(offer: OfferJson): string {
  if (offer.label.trim()) return offer.label.trim();
  return offer.kind === "code" ? "Your code" : "The offer";
}

/**
 * The one press that counts, kind by kind: a link that opens, a code that
 * copies, a file that saves. A message has no press, only its words.
 */
function OfferAction({
  offer,
  viewerEmail,
  onConvert,
}: {
  offer: OfferJson;
  viewerEmail: string;
  onConvert?: () => void;
}): ReactNode {
  const [copied, setCopied] = useState(false);
  const back = useRef(0);
  useEffect(() => () => window.clearTimeout(back.current), []);
  const label = offer.label.trim();

  if (offer.kind === "code") {
    if (!offer.code) return null;
    const copy = () => {
      onConvert?.();
      setCopied(true);
      window.clearTimeout(back.current);
      back.current = window.setTimeout(() => setCopied(false), COPIED_MS);
      try {
        void navigator.clipboard?.writeText(offer.code ?? "");
      } catch {
        /* a browser that will not hand over the clipboard still shows the code */
      }
    };
    return (
      <div className="w-ofcode">
        <span className="w-num">{offer.code}</span>
        <button type="button" className="w-ofbtn" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    );
  }
  if (offer.kind === "download" && offer.file) {
    return (
      <a className="w-ofbtn" href={fileHref(offer.file)} download target="_blank" rel="noreferrer" onClick={() => onConvert?.()}>
        {label || "Get the file"}
        <IcSave />
      </a>
    );
  }
  if ((offer.kind === "call" || offer.kind === "buy") && offer.url) {
    return (
      <a className="w-ofbtn" href={withEmail(offer.url, viewerEmail)} target="_blank" rel="noopener noreferrer" onClick={() => onConvert?.()}>
        {label || (offer.kind === "call" ? "Book a call" : "Get started")}
        <IcArrow />
      </a>
    );
  }
  return null;
}

/** The note itself, playing from the press that opened it. */
function OfferNoteVideo({
  video,
  host,
  onEnded,
}: {
  video: OfferVideo;
  host: string;
  onEnded: () => void;
}): ReactNode {
  const ref = useRef<HTMLVideoElement>(null);
  const stream = useNoteStream(ref, video.url);
  return (
    <video
      ref={ref}
      className="w-ofvideo"
      src={stream ? undefined : video.url}
      poster={video.poster ?? undefined}
      controls
      autoPlay
      playsInline
      aria-label={`A note from ${host}`}
      onEnded={onEnded}
    />
  );
}

/**
 * The host's short video, as a poster with a play mark that becomes the video
 * itself when pressed. Playing it holds the talk, which `onPlayNote` does.
 */
function OfferNote({
  offer,
  host,
  onPlayNote,
  onNote,
}: {
  offer: OfferJson;
  host: string;
  onPlayNote?: () => void;
  /** The note started, or played to the end: what the tags are read from. */
  onNote?: (state: "play" | "end") => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const video = offer.video;
  if (!video) return null;
  if (open) return <OfferNoteVideo video={video} host={host} onEnded={() => onNote?.("end")} />;
  return (
    <button
      type="button"
      className="w-ofnote"
      aria-label={`Play the note from ${host}, ${mmss(video.duration_sec)}`}
      onClick={() => {
        onPlayNote?.();
        onNote?.("play");
        setOpen(true);
      }}
    >
      <span className="w-ofposter" style={video.poster ? { backgroundImage: `url("${video.poster}")` } : undefined}>
        <IcPlay />
      </span>
      <span className="w-ofnotet">
        <b>Play the note</b>
        <small className="w-num">{mmss(video.duration_sec)}</small>
      </span>
    </button>
  );
}

/** The card in the corner of the picture, while the talk plays on. */
export function OfferCard({
  offer,
  hostName,
  viewerEmail,
  onConvert,
  onClose,
  onPlayNote,
  onNote,
}: {
  offer: OfferJson;
  hostName: string;
  viewerEmail: string;
  onConvert?: () => void;
  onClose: () => void;
  onPlayNote?: () => void;
  onNote?: (state: "play" | "end") => void;
}): ReactNode {
  const host = firstName(hostName);
  return (
    <div className="w-ofcard" role="dialog" aria-label={`A note from ${host}`}>
      <button type="button" className="w-ofx" aria-label="Close" onClick={onClose}>
        <IcCross />
      </button>
      <span className="w-ofkick">A note from <bdi>{host}</bdi></span>
      <OfferNote offer={offer} host={host} onPlayNote={onPlayNote} onNote={onNote} />
      {offer.text ? <p className="w-ofwords">{offer.text}</p> : null}
      <OfferAction offer={offer} viewerEmail={viewerEmail} onConvert={onConvert} />
    </div>
  );
}

/** The card folded away: the button's own words on a tab at the edge. */
export function OfferTab({ offer, onOpen }: { offer: OfferJson; onOpen: () => void }): ReactNode {
  return (
    <button type="button" className="w-oftab" onClick={onOpen}>
      <span className="w-ofdot" />
      {offerTabLabel(offer)}
    </button>
  );
}

/** The end of the talk: the offer, large, and one line to watch again. */
export function EndCard({
  offer,
  hostName,
  viewerEmail,
  onConvert,
  onAgain,
}: {
  offer: OfferJson | null;
  hostName: string;
  viewerEmail: string;
  onConvert?: () => void;
  onAgain: () => void;
}): ReactNode {
  const host = firstName(hostName);
  return (
    <div className="w-endcard" role="dialog" aria-label="The talk has ended">
      <div className="w-endin">
        <span className="w-endkick">That's the talk</span>
        {offer ? (
          <>
            <h3>A note from <bdi>{host}</bdi></h3>
            <OfferNote offer={offer} host={host} />
            {offer.text ? <p className="w-ofwords">{offer.text}</p> : null}
            <OfferAction offer={offer} viewerEmail={viewerEmail} onConvert={onConvert} />
          </>
        ) : null}
        <button type="button" className="w-again" onClick={onAgain}>
          <IcAgain />
          Watch again
        </button>
      </div>
    </div>
  );
}
